import { useCallback } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { reportErrorToLogFetch } from 'academy-common-lib/utils/errorUtils';
import ExamTimer from './ExamTimer';
import UsageTracker from '../../../utils/UsageTracker';
import { useResumeExam, useValidateExam } from '../examsQueries';
import { BaseContentControlWrapper } from '../../../components/ContentControlWrapper';
import { ContactUsLink } from '../../../components/ContactUsLink';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// @bend-expect trellis-tokenized
const ContentWrapper = styled.div.withConfig({
  displayName: "ExamResumeScreen__ContentWrapper"
})(({
  theme
}) => ({
  backgroundColor: theme.color['fill-surface-default'],
  padding: '10% 5%'
}));
const ExamResumeScreen = ({
  track,
  mostRecentExamAttempt
}) => {
  var _track$exam;
  const {
    id: trackId,
    language
  } = track;
  const [validateExam, {
    validateExamLoading,
    validateExamError
  }] = useValidateExam(trackId, language);
  const [resumeExam, {
    resumeExamLoading,
    resumeExamError
  }] = useResumeExam();
  const totalQuestionCount = ((_track$exam = track.exam) === null || _track$exam === void 0 ? void 0 : _track$exam.length) || 0;
  const answeredQuestionCount = (mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 ? void 0 : mostRecentExamAttempt.questionSubmissions.length) || 0;
  const remainingQuestionCount = totalQuestionCount - answeredQuestionCount;
  const handleResume = useCallback(() => {
    var _track$exam3, _mostRecentExamAttemp2;
    if (!mostRecentExamAttempt) {
      return null;
    }
    if (answeredQuestionCount >= totalQuestionCount) {
      return validateExam(mostRecentExamAttempt.id).then(() => {
        var _track$exam2, _mostRecentExamAttemp;
        UsageTracker.trackStandalone('Exam Interaction', {
          action: 'completed',
          examId: (_track$exam2 = track.exam) === null || _track$exam2 === void 0 ? void 0 : _track$exam2.id,
          examLength: mostRecentExamAttempt.questions.length,
          trackId: trackId === null || trackId === void 0 ? void 0 : trackId.toString(),
          trackName: track === null || track === void 0 ? void 0 : track.name,
          examAttemptId: mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 || (_mostRecentExamAttemp = mostRecentExamAttempt.id) === null || _mostRecentExamAttemp === void 0 ? void 0 : _mostRecentExamAttemp.toString(),
          language: readUnhandledEnumCase(mostRecentExamAttempt.language)
        });
        Metrics.counter('exam-completed', {
          language: readUnhandledEnumCase(mostRecentExamAttempt.language)
        }).increment();
      }).catch(error => {
        Metrics.counter('exam-mutation-call-error', {
          action: 'exam-resume-validation-error'
        }).increment();
        reportErrorToLogFetch({
          loggingTitle: 'Validate Exam Error',
          action: 'exam-resume-validation-error',
          error
        });
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.exams.resume.resumeExamError",
            options: {
              contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
            }
          })
        });
      });
    }
    UsageTracker.trackStandalone('Exam Interaction', {
      action: 'resume',
      examId: (_track$exam3 = track.exam) === null || _track$exam3 === void 0 ? void 0 : _track$exam3.id,
      examLength: mostRecentExamAttempt.questions.length,
      trackId: trackId === null || trackId === void 0 ? void 0 : trackId.toString(),
      trackName: track === null || track === void 0 ? void 0 : track.name,
      examAttemptId: mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 || (_mostRecentExamAttemp2 = mostRecentExamAttempt.id) === null || _mostRecentExamAttemp2 === void 0 ? void 0 : _mostRecentExamAttemp2.toString()
    });
    return resumeExam(Number(trackId), mostRecentExamAttempt.id, mostRecentExamAttempt.language).then(() => {
      Metrics.counter('exam-resumed', {
        language: readUnhandledEnumCase(mostRecentExamAttempt.language)
      }).increment();
    }).catch(error => {
      Metrics.counter('exam-mutation-call-error', {
        action: 'exam-resume-error'
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Resume Exam Error',
        action: 'exam-resume-error',
        error
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.exams.resume.resumeExamError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  }, [answeredQuestionCount, mostRecentExamAttempt, resumeExam, totalQuestionCount, track, trackId, validateExam]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(ContentWrapper, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        blockSize: "100%",
        flexDirection: "row",
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(Pictogram, {
          marginBlockStart: "500",
          marginInlineEnd: "800",
          name: "Registration"
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.exams.resume.heading"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.exams.resume.remainingQuestionCount",
            options: {
              count: remainingQuestionCount
            }
          }), /*#__PURE__*/_jsx(ExamTimer, {
            mostRecentExamAttempt: mostRecentExamAttempt,
            track: track
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      borderStyle: "none",
      children: /*#__PURE__*/_jsx(LoadingButton, {
        failed: !!validateExamError || !!resumeExamError,
        loading: validateExamLoading || resumeExamLoading,
        onClick: () => {
          void handleResume();
        },
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.resume.resumeExam"
        })
      })
    })]
  });
};
export default ExamResumeScreen;